import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import {
  failCountryValidation,
  failStateValidation,
  userAcceptedSuggestedAddress,
} from "./checkoutPhysicalAddress/action-creators";
import { failNoomClinicalAgeValidation } from "./paymentEnrollmentFormActions";

/**
 * Payment type constants
 */
export enum paymentTypes {
  CREDIT_CARD = "CREDIT_CARD",
  PAYPAL = "PAYPAL",
  SEPA = "SEPA",
}

export interface BillingAddress {
  country: string;
  city: string;
  region: string;
  address1: string;
  address2: string;
  zipcode: string;
}

export enum accordionStates {
  IN_PROGRESS = "IN_PROGRESS",
  LOCKED = "LOCKED",
  COMPLETE = "COMPLETE",
  ERROR = "ERROR",
}

export type AccordionInfo = {
  name: string;
  opened: boolean;
  nextAccordion?: string;
  showContinueButton?: boolean;
  step: number;
  state: accordionStates;
};

export const accordionsInitialState = {
  personalizedCourse: {
    name: "personalizedCourse",
    opened: true,
    nextAccordion: "paymentInfo",
    showContinueButton: true,
    state: accordionStates.IN_PROGRESS,
  },
  paymentInfo: {
    name: "paymentInfo",
    opened: false,
    nextAccordion: "accountInfo",
    showContinueButton: true,
    state: accordionStates.LOCKED,
  },
  accountInfo: {
    name: "accountInfo",
    opened: false,
    nextAccordion: "reviewTermsAndSubmit",
    showContinueButton: true,
    state: accordionStates.LOCKED,
  },
  reviewTermsAndSubmit: {
    name: "reviewTermsAndSubmit",
    opened: false,
    showContinueButton: false,
    state: accordionStates.LOCKED,
  },
};

export type AccordionType = keyof typeof accordionsInitialState;

export interface CheckoutState {
  salesTaxAmount: number;
  salesTaxState: string;
  salesTaxRate: number;
  paypalAccount: string;
  paypalError?: string;
  paymentType?: paymentTypes;
  purchaseErrorCode?: errorConstants | Error | "";
  processing: boolean;
  goalMonth: string;
  // TODO(patrick): Move these modal states out of redux and into the component states if possible.
  madeTrialPriceChoice: boolean;
  isTimerExpired: boolean;
  isTaxInclusive: boolean;
  walletPay: boolean;
  applePay: boolean;
  googlePay: boolean;

  billingName?: string;
  billingAddress?: BillingAddress;
  billingAddressErrors?: {
    COUNTRY: boolean;
    CITY: boolean;
    REGION: boolean;
    ADDRESS1: boolean;
    ZIPCODE: boolean;
  };

  showInvalidAgeModal?: boolean;
  showEnrollmentErrors?: boolean;
  showPaymentInfoErrors?: boolean;
  showReviewErrors?: boolean;

  stripeError?: Record<string, boolean | string>;
  sepaError?: Record<string, boolean | string>;
  // Used to handle any changes in the stripe universal form
  stripeUniversal: {
    // Sets the form to read-only.
    // The new universal payments approach is to create a payment intent first, then charge it.
    // Since our backend handles email validation after a payment intent is created, the frontend
    // will disable the form so they can't make changes to their payment method unless they refresh
    // to avoid edge case behaviours.
    formLocked: boolean;
  };

  userStartedEnteringPaymentInfo: boolean;
  toggledCoursePackOffer: boolean;
  multiUserPlan: {
    toggled: boolean;
    secondaryUserSameAddressConfirmation?: boolean;
  };

  accordions?: Record<AccordionType, AccordionInfo>;
  currentAccordion: string;
}

export const initialState: CheckoutState = {
  salesTaxAmount: 0,
  salesTaxState: "",
  salesTaxRate: 0,
  paypalAccount: "",
  processing: false,
  goalMonth: "",
  madeTrialPriceChoice: false,
  isTimerExpired: false,
  isTaxInclusive: false,
  walletPay: false,
  applePay: false,
  googlePay: false,
  userStartedEnteringPaymentInfo: false,
  stripeUniversal: { formLocked: false },
  toggledCoursePackOffer: false,
  multiUserPlan: {
    toggled: false,
    secondaryUserSameAddressConfirmation: null,
  },
  currentAccordion: "personalizedCourse",
};

const checkoutSlice = createSlice({
  name: "checkout",
  initialState,
  reducers: {
    updateCheckoutState(state, action: PayloadAction<Partial<CheckoutState>>) {
      return { ...state, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(failCountryValidation, (state) => ({
        ...state,
        toggledCoursePackOffer: false,
      }))
      .addCase(failStateValidation, (state) => ({
        ...state,
        toggledCoursePackOffer: false,
      }))
      .addCase(userAcceptedSuggestedAddress, (state, action) => {
        const { isSameAsBillingAddress, suggestedAddress } = action.payload;
        if (!isSameAsBillingAddress) {
          return state;
        }
        const ret = { ...state };
        ret.billingAddress = {
          ...ret.billingAddress,
          ...suggestedAddress,
        };
        return ret;
      })
      .addCase(failNoomClinicalAgeValidation, (state) => ({
        ...state,
        toggledCoursePackOffer: false,
      }));
  },
});

export const { updateCheckoutState } = checkoutSlice.actions;

export default checkoutSlice;
